import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Users,
  CalendarDays,
  Trophy,
  History,
  Settings,
  LogOut,
  Shield,
  Menu,
  X,
  Swords,
  Sparkles,
  CreditCard,
  Image as ImageIcon,
  FileBarChart,
  Medal,
  ScrollText,
  UserCog,
  UserCheck,
} from "lucide-react";
import { useEffect, useState } from "react";
import NotificationsBell from "@/components/NotificationsBell";
import api from "@/lib/api";

const links = [
  { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/matches", label: "Matchs", icon: CalendarDays, testid: "nav-matches" },
  { to: "/officiels", label: "Matchs officiels", icon: Medal, testid: "nav-officiels" },
  { to: "/gala", label: "Matchs de Gala", icon: Sparkles, testid: "nav-gala" },
  { to: "/players", label: "Joueurs", icon: Users, testid: "nav-players" },
  { to: "/leaderboard", label: "Buteurs", icon: Trophy, testid: "nav-leaderboard" },
  { to: "/duel", label: "Duel B vs R", icon: Swords, testid: "nav-duel" },
  { to: "/gallery", label: "Galerie", icon: ImageIcon, testid: "nav-gallery" },
  { to: "/history", label: "Historique", icon: History, testid: "nav-history" },
  { to: "/paiement", label: "Paiement", icon: CreditCard, testid: "nav-payments" },
  { to: "/report", label: "Bilan saison", icon: FileBarChart, testid: "nav-report" },
];

export default function Layout({ children }) {
  const { user, logout, settings } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const isCoach = user?.role === "coach" || user?.role === "admin";

  useEffect(() => {
    if (!isCoach) {
      setPendingCount(0);
      return;
    }
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/players/pending-count");
        if (alive) setPendingCount(data.count || 0);
      } catch (e) {}
    };
    load();
    const t = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [isCoach]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const NavItems = () => (
    <>
      {links.map((l) => (
        <NavLink
          key={l.to}
          to={l.to}
          data-testid={l.testid}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-2 lg:gap-2.5 px-2.5 lg:px-3 xl:px-4 py-2 lg:py-2.5 xl:py-3 rounded-lg text-[11px] lg:text-xs xl:text-sm font-semibold uppercase tracking-wide transition-colors ${
              isActive
                ? "bg-[#E5B85C] text-[#0B0F17]"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`
          }
        >
          <l.icon className="w-4 h-4 shrink-0" />
          <span className="truncate">{l.label}</span>
        </NavLink>
      ))}
      {isCoach && (
        <NavLink
          to="/manage"
          data-testid="nav-manage"
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-2 lg:gap-2.5 px-2.5 lg:px-3 xl:px-4 py-2 lg:py-2.5 xl:py-3 rounded-lg text-[11px] lg:text-xs xl:text-sm font-semibold uppercase tracking-wide transition-colors ${
              isActive
                ? "bg-[#E5B85C] text-[#0B0F17]"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`
          }
        >
          <UserCog className="w-4 h-4 shrink-0" />
          <span className="truncate">Gestion</span>
        </NavLink>
      )}
      {isCoach && (
        <NavLink
          to="/manage/pending"
          data-testid="nav-pending"
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-2 lg:gap-2.5 px-2.5 lg:px-3 xl:px-4 py-2 lg:py-2.5 xl:py-3 rounded-lg text-[11px] lg:text-xs xl:text-sm font-semibold uppercase tracking-wide transition-colors ${
              isActive
                ? "bg-[#E5B85C] text-[#0B0F17]"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`
          }
        >
          <UserCheck className="w-4 h-4 shrink-0" />
          <span className="truncate flex-1">Inscriptions</span>
          {pendingCount > 0 && (
            <span
              data-testid="pending-badge"
              className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-mono-stat font-bold"
            >
              {pendingCount}
            </span>
          )}
        </NavLink>
      )}
      {isCoach && (
        <NavLink
          to="/manage/paiements"
          data-testid="nav-payments-admin"
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-2 lg:gap-2.5 px-2.5 lg:px-3 xl:px-4 py-2 lg:py-2.5 xl:py-3 rounded-lg text-[11px] lg:text-xs xl:text-sm font-semibold uppercase tracking-wide transition-colors ${
              isActive
                ? "bg-[#E5B85C] text-[#0B0F17]"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`
          }
        >
          <CreditCard className="w-4 h-4 shrink-0" />
          <span className="truncate">Paiements club</span>
        </NavLink>
      )}
      {isCoach && (
        <NavLink
          to="/audit"
          data-testid="nav-audit"
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-2 lg:gap-2.5 px-2.5 lg:px-3 xl:px-4 py-2 lg:py-2.5 xl:py-3 rounded-lg text-[11px] lg:text-xs xl:text-sm font-semibold uppercase tracking-wide transition-colors ${
              isActive
                ? "bg-[#E5B85C] text-[#0B0F17]"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`
          }
        >
          <ScrollText className="w-4 h-4 shrink-0" />
          <span className="truncate">Journal</span>
        </NavLink>
      )}
      {isCoach && (
        <NavLink
          to="/settings"
          data-testid="nav-settings"
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-2 lg:gap-2.5 px-2.5 lg:px-3 xl:px-4 py-2 lg:py-2.5 xl:py-3 rounded-lg text-[11px] lg:text-xs xl:text-sm font-semibold uppercase tracking-wide transition-colors ${
              isActive
                ? "bg-[#E5B85C] text-[#0B0F17]"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`
          }
        >
          <Settings className="w-4 h-4 shrink-0" />
          <span className="truncate">Paramètres</span>
        </NavLink>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-[#0B0F17] text-white flex">
      {/* Sidebar : compact at md (petites tablettes), medium lg, full xl */}
      <aside className="hidden md:flex w-52 lg:w-56 xl:w-72 flex-col border-r border-white/10 bg-[#0B0F17] sticky top-0 h-screen">
        <div className="p-3 lg:p-4 xl:p-6 border-b border-white/10 flex items-center gap-2 lg:gap-3">
          {settings.app_logo_url ? (
            <img
              src={settings.app_logo_url}
              alt="Logo"
              className="w-9 h-9 lg:w-10 lg:h-10 xl:w-11 xl:h-11 rounded-full object-cover"
              data-testid="app-logo"
            />
          ) : (
            <div className="w-9 h-9 lg:w-10 lg:h-10 xl:w-11 xl:h-11 rounded-full bg-[#E5B85C] flex items-center justify-center">
              <Shield className="w-5 h-5 lg:w-6 lg:h-6 text-[#0B0F17]" strokeWidth={2.5} />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-display font-black text-sm lg:text-base xl:text-xl uppercase tracking-tight leading-none truncate">
              {settings.app_name || "ASS les Régionnaires"}
            </div>
            <div className="hidden xl:block text-[10px] font-mono-stat uppercase tracking-[0.2em] text-slate-500 mt-1">
              Club Manager
            </div>
          </div>
        </div>

        <nav className="flex-1 p-2 lg:p-3 xl:p-4 space-y-1 overflow-y-auto">
          <NavItems />
        </nav>

        <div className="p-2 lg:p-3 xl:p-4 border-t border-white/10">
          <NavLink
            to="/profile"
            data-testid="nav-profile"
            className="block px-4 py-3 rounded-lg bg-slate-900 mb-2 hover:bg-slate-800 transition-colors"
          >
            <div className="text-xs text-slate-400 font-mono-stat uppercase tracking-widest">
              {user?.role === "admin" ? "Directeur technique" : user?.role === "coach" ? "Entraîneur" : "Joueur"}
            </div>
            <div className="font-bold text-sm truncate mt-0.5" data-testid="user-name">{user?.name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            <div className="text-[10px] text-[#E5B85C] font-mono-stat uppercase tracking-widest mt-1">
              Modifier mon profil →
            </div>
          </NavLink>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-[#DC2626] text-white text-sm font-semibold uppercase tracking-wide transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#0B0F17]/95 backdrop-blur-xl border-b border-white/10 h-16 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          {settings.app_logo_url ? (
            <img src={settings.app_logo_url} alt="Logo" className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-[#E5B85C] flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#0B0F17]" strokeWidth={2.5} />
            </div>
          )}
          <div className="font-display font-black text-lg uppercase">
            {settings.app_name || "ASS les Régionnaires"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <NotificationsBell user={user} />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="p-2 rounded-lg bg-slate-800"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-30 bg-[#0B0F17]/95 backdrop-blur-xl pt-20 px-4 pb-8 overflow-y-auto"
          data-testid="mobile-drawer"
        >
          <div className="space-y-1">
            <NavItems />
          </div>
          <NavLink
            to="/profile"
            onClick={() => setOpen(false)}
            data-testid="mobile-nav-profile"
            className="mt-6 block px-4 py-3 rounded-lg bg-slate-900 hover:bg-slate-800 transition-colors"
          >
            <div className="text-xs text-slate-400 font-mono-stat uppercase">
              {user?.role === "admin" ? "Directeur technique" : user?.role === "coach" ? "Entraîneur" : "Joueur"}
            </div>
            <div className="font-bold text-sm mt-0.5">{user?.name}</div>
            <div className="text-[10px] text-[#E5B85C] font-mono-stat uppercase tracking-widest mt-1">
              Modifier mon profil →
            </div>
          </NavLink>
          <button
            data-testid="mobile-logout-button"
            onClick={handleLogout}
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#DC2626] text-white font-semibold uppercase"
          >
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      )}

      <main className="flex-1 min-w-0 pt-16 md:pt-0 relative overflow-hidden">
        <div className="hidden md:flex absolute top-4 right-4 lg:right-6 z-20 gap-2">
          <NotificationsBell user={user} />
        </div>
        {settings.app_logo_url && (
          <div
            aria-hidden="true"
            data-testid="app-bg-watermark"
            className="pointer-events-none absolute inset-0 flex items-center justify-center z-0"
          >
            <img
              src={settings.app_logo_url}
              alt=""
              className="w-[min(85vw,780px)] h-[min(85vw,780px)] object-contain opacity-[0.045] mix-blend-screen select-none"
              draggable="false"
            />
          </div>
        )}
        <div className="relative z-10 p-3 sm:p-5 md:p-5 lg:p-6 xl:p-10 max-w-7xl">{children}</div>
      </main>
    </div>
  );
}
